import { MentionData } from "@draft-js-plugins/mention"
import { CollectionNameEnum, CommentItem, Employee, OperationTypeEnum } from "@finway-group/shared/lib/models"
import { Comment } from "antd"
import { CompositeDecorator, ContentState, DraftDecorator, EditorState } from "draft-js"
import React, { Suspense } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import {
    useCreateDeliveryNoteCommentMutation,
    useDeleteDeliveryNoteCommentsMutation,
    useUpdateDeliveryNoteCommentsMutation,
} from "Features/pages/deliveryNotes/queries/deliveryNote.queries"
import UserImage from "Components/UserImage"
import { useUpdatePurchaseOrderCommentsMutation } from "Features/pages/purchaseOrders/queries/purchaseOrder.queries"
import { FINWAY_ADMIN_EMAIL } from "Shared/config/consts"
import { useEmployees } from "Shared/hooks/employee.hooks"
import { useUpdateVendorComments } from "Shared/queries/vendors/vendor.mutations"
import { NotificationService, UserService } from "Shared/services"
import { NotificationTypeEnum } from "Shared/services/notification.service"
import { updateComments as updateExpenseComment } from "Shared/store/actions/expense/expenseActions"
import { isEmptyString, sortItemsDescByDateField } from "Shared/utils/helper.utils"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"

import { useEditorDecoratorProps } from "./useEditorDecoratorsProps"

const CommentEditor = React.lazy(() => import("./comment.editor"))
const CommentList = React.lazy(() => import("./comment.list"))

interface CommentContainerInterface {
    object: { id: string }
    type: CollectionNameEnum
    comments: Array<CommentItem>
}

export const CommentContainer = ({ object, type, comments: commentsUnsorted }: CommentContainerInterface) => {
    const comments = sortItemsDescByDateField(commentsUnsorted, "datePosted")
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const loggedInUser = UserService.getLoggedInEmployeeProfile()
    const employees = useEmployees({ excludeDeleted: true })

    const decoratorProps = useEditorDecoratorProps()
    const pluginDecorators = decoratorProps.plugins.reduce<Array<DraftDecorator>>((acc: any, p: any) => [...acc, ...p.decorators], [])
    const [editorState, setEditorState] = useStateIfMounted<EditorState>(EditorState.createWithContent(ContentState.createFromText(""), new CompositeDecorator(pluginDecorators)))

    const updateVendorCommentMutation = useUpdateVendorComments()

    const updatePurchaseOrderCommentMutation = useUpdatePurchaseOrderCommentsMutation()

    const createDeliveryNoteCommentMutation = useCreateDeliveryNoteCommentMutation()
    const updateDeliveryNoteCommentMutation = useUpdateDeliveryNoteCommentsMutation()
    const deleteDeliveryNoteCommentMutation = useDeleteDeliveryNoteCommentsMutation()

    const mapToMentionData = (list: Array<Employee>) =>
        list
            .filter((employee: Employee) => employee && employee.id !== loggedInUser.id && employee.email !== FINWAY_ADMIN_EMAIL)
            .map(({ getFullName, img, id }: Employee) => ({
                name: getFullName?.(),
                avatar: img && img.length ? img : `${window.location.origin}/images/default.svg`,
                link: id,
            })) as Array<MentionData>

    const onWriteError = () => {
        NotificationService.send(NotificationTypeEnum.ERROR, t("error:comment.save.title"), t("error:comment.save.message"))
    }

    const handleSubmit = (newComment: string) => {
        if (isEmptyString(newComment)) {
            NotificationService.send(NotificationTypeEnum.ERROR, t("error:comment.save.title"), t("error:comment.save.message"))
            return
        }

        const comment = new CommentItem({
            postedBy: loggedInUser.id,
            message: newComment,
            datePosted: new Date(),
            isAutoGenerated: false,
        })
        const newComments = [comment, ...comments]

        switch (type) {
            case CollectionNameEnum.EXPENSE:
                dispatch(updateExpenseComment(object.id, comment, OperationTypeEnum.INSERT))
                break
            case CollectionNameEnum.VENDOR:
                updateVendorCommentMutation.mutate({ vendorId: object.id, vendorComments: newComments }, { onError: onWriteError })
                break
            case CollectionNameEnum.PURCHASE_ORDER:
                updatePurchaseOrderCommentMutation.mutate({ objectId: object.id, comments: newComments }, { onError: onWriteError })
                break
            case CollectionNameEnum.DELIVERY_NOTE:
                createDeliveryNoteCommentMutation.mutate({ deliveryNoteId: object.id, comment }, { onError: onWriteError })
                break
            default:
                onWriteError()
                break
        }
    }

    const handleUpdate = (index: number, newComment: string) => {
        if (isEmptyString(newComment)) {
            NotificationService.send(NotificationTypeEnum.ERROR, t("error:comment.update.title"), t("error:comment.update.message"))
            return
        }

        const newComments = [...comments]
        const newCommentItem = new CommentItem({
            ...newComments[index],
            message: newComment,
            isEdited: true,
        })
        newComments[index] = newCommentItem

        switch (type) {
            case CollectionNameEnum.EXPENSE:
                dispatch(updateExpenseComment(object.id, newComments[index], OperationTypeEnum.UPDATE))
                break
            case CollectionNameEnum.VENDOR:
                updateVendorCommentMutation.mutate({ vendorId: object.id, vendorComments: newComments }, { onError: onWriteError })
                break
            case CollectionNameEnum.PURCHASE_ORDER:
                updatePurchaseOrderCommentMutation.mutate({ objectId: object.id, comments: newComments }, { onError: onWriteError })
                break
            case CollectionNameEnum.DELIVERY_NOTE:
                updateDeliveryNoteCommentMutation.mutate({ deliveryNoteId: object.id, commentId: comments[index]._id!, comment: newCommentItem }, { onError: onWriteError })
                break
            default:
                onWriteError()
                break
        }
    }

    const handleDelete = (index: number) => {
        const comment = comments[index]
        const newComments = [...comments]
        newComments.splice(index, 1)

        switch (type) {
            case CollectionNameEnum.EXPENSE:
                dispatch(updateExpenseComment(object.id, comment, OperationTypeEnum.DELETE))
                break
            case CollectionNameEnum.VENDOR:
                updateVendorCommentMutation.mutate({ vendorId: object.id, vendorComments: newComments }, { onError: onWriteError })
                break
            case CollectionNameEnum.PURCHASE_ORDER:
                updatePurchaseOrderCommentMutation.mutate({ objectId: object.id, comments: newComments }, { onError: onWriteError })
                break
            case CollectionNameEnum.DELIVERY_NOTE:
                deleteDeliveryNoteCommentMutation.mutate({ deliveryNoteId: object.id, commentId: comments[index]._id! }, { onError: onWriteError })
                break
            default:
                onWriteError()
                break
        }
    }

    return (
        <div className="h-full min-h-300">
            <Comment
                avatar={<UserImage user={loggedInUser} size={30} />}
                content={
                    <CommentEditor
                        decoratorProps={decoratorProps}
                        mentions={mapToMentionData(employees)}
                        editorState={editorState}
                        setEditorState={setEditorState}
                        onSubmit={handleSubmit}
                    />
                }
            />

            {comments.length > 0 && (
                <Suspense fallback={<></>}>
                    <CommentList comments={comments} mentions={mapToMentionData(employees)} onUpdate={handleUpdate} onDelete={handleDelete} />
                </Suspense>
            )}
        </div>
    )
}
